import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FaArchive, FaTrash } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import PermissionGuard from './PermissionGuard';
import { PERMISSIONS } from '../constants/permissions';
import { formatCarePlanId, formatQuotationId } from '../utils/displayIds';
import '../Pages/CareRecipients.css';

const C = {
    dark: '#131926',
    darkMid: '#1e2d40',
    aqua: '#55fff4',
    accent: '#2a7f8a',
    grey: '#d1d2dc',
    textMuted: '#6b7280',
    green: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    blue: '#3b82f6',
    border: '#eaf2f4',
    rowAlt: '#f4fafa',
    headerBg: '#c5e4e7',
    headerText: '#356167',
    surface: '#f4fafa',
    rowHover: '#eaf4f5',
};

const TH = {
    padding: '11px 14px',
    color: C.headerText,
    fontWeight: 600,
    fontSize: '0.76rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    background: 'inherit',
    border: 'none',
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

const TD = {
    padding: '11px 14px',
    borderBottom: `1px solid ${C.border}`,
    fontSize: '0.9rem',
    color: '#1e3a5f',
    verticalAlign: 'middle',
};

function quotationDetailPath(customerId, quote) {
    const dateParts = quote.date_created ? String(quote.date_created).split(' ')[0] : '';
    if (quote.care_plan_id) {
        return `/careplans/${customerId}/${quote.care_plan_id}/quote/${quote.quotation_id}`;
    }
    return `/quotation/${customerId}/${dateParts}/${quote.quotation_id}`;
}

function Quotations() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentTenant, sites } = useTenant();
    const { hasPermission } = usePermissions();
    const [quotations, setQuotations] = useState([]);
    const [archivedQuotations, setArchivedQuotations] = useState([]);
    const [removedQuotations, setRemovedQuotations] = useState([]);
    const [activeTab, setActiveTab] = useState('active');
    const [customerName, setCustomerName] = useState('');
    const [loading, setLoading] = useState({});
    const [latestActiveQuotationId, setLatestActiveQuotationId] = useState(null);

    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [quotationToArchive, setQuotationToArchive] = useState(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [quotationToRemove, setQuotationToRemove] = useState(null);
    const [showArchiveAllModal, setShowArchiveAllModal] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

    const showToast = (message, variant = 'success') => setToast({ show: true, message, variant });

    useEffect(() => {
        if (!toast.show) return;
        const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
        return () => clearTimeout(timer);
    }, [toast.show]);

    const fetchQuotations = async () => {
        const tenantName = currentTenant?.name;

        try {
            const carePlansResponse = await fetchWithAuth(`${API_BASE_URL}/careplans/${id}`);
            const carePlansData = await carePlansResponse.json();

            let carePlansArray = [];
            if (carePlansData.care_plans) carePlansArray = carePlansData.care_plans;
            else if (Array.isArray(carePlansData)) carePlansArray = carePlansData;
            else if (carePlansData.carePlans) carePlansArray = carePlansData.carePlans;

            const carePlanInfoMap = {};
            carePlansArray.forEach(plan => {
                const planId = plan.care_plan_id || plan.CarePlanID;
                if (planId) {
                    carePlanInfoMap[planId] = {
                        tenant_name: plan.tenant_name || plan.TenantName,
                        site_name: plan.site_name || plan.SiteName,
                        tenant_site_id: plan.tenant_site_id || plan.TenantSiteID
                    };
                }
            });

            const response = await fetchWithAuth(`${API_BASE_URL}/quotation/${id}`);
            const data = await response.json();
            const quotationsData = data.Quotations || data.quotations;

            if (quotationsData && Array.isArray(quotationsData)) {
                const tenantFilteredQuotes = quotationsData.filter(quote => {
                    const planInfo = carePlanInfoMap[quote.care_plan_id];
                    if (planInfo && planInfo.tenant_name) return planInfo.tenant_name === tenantName;
                    return false;
                });

                const enrichedQuotations = tenantFilteredQuotes.map(quote => {
                    const planInfo = carePlanInfoMap[quote.care_plan_id];
                    return { ...quote, tenant_name: planInfo?.tenant_name || null, site_name: planInfo?.site_name || null };
                });

                const nonRemovedQuotes = enrichedQuotations.filter(q => q.status !== 2);
                const sortFn = (a, b) => {
                    const diff = new Date(b.date_created || 0) - new Date(a.date_created || 0);
                    return diff !== 0 ? diff : b.quotation_id - a.quotation_id;
                };

                const removedQuotes = enrichedQuotations.filter(q => q.status === 2).sort(sortFn);
                const mainQuotations = nonRemovedQuotes.filter(q => q.status !== 3).sort(sortFn);
                const archiveQuotations = nonRemovedQuotes.filter(q => q.status === 3).sort(sortFn);

                setQuotations(mainQuotations);
                setArchivedQuotations(archiveQuotations);
                setRemovedQuotations(removedQuotes);
                setCustomerName(data.customerName || data.customer_name || 'Unknown');

                const activeOnes = mainQuotations.filter(q => q.status === 1);
                if (activeOnes.length > 0) {
                    setLatestActiveQuotationId(activeOnes.reduce((a, b) => b.quotation_id > a.quotation_id ? b : a).quotation_id);
                } else {
                    setLatestActiveQuotationId(null);
                }
            } else {
                setQuotations([]);
                setArchivedQuotations([]);
                setRemovedQuotations([]);
                setLatestActiveQuotationId(null);
            }
        } catch (error) {
            console.error('Error fetching quotations:', error);
            setQuotations([]);
            setArchivedQuotations([]);
        }
    };

    const fetchCustomerDetails = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/care-recipients/${id}`);
            if (response.ok) {
                const data = await response.json();
                setCustomerName(`${data.name} ${data.surname}`);
            }
        } catch (error) {
            console.error('Error fetching customer details:', error);
        }
    };

    const handleActivateQuotation = async (quotation_id) => {
        setLoading(prev => ({ ...prev, [quotation_id]: true }));
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/quotes/${quotation_id}/set-active`, { method: 'PUT' });
            if (response.ok) {
                showToast('Quotation activated successfully!');
                await fetchQuotations();
            } else {
                showToast('Failed to activate quotation.', 'danger');
            }
        } catch (error) {
            showToast('Error activating quotation.', 'danger');
        } finally {
            setLoading(prev => ({ ...prev, [quotation_id]: false }));
        }
    };

    const handleArchiveClick = (quotation) => { setQuotationToArchive(quotation); setShowArchiveModal(true); };

    const handleArchiveConfirm = async () => {
        if (!quotationToArchive) return;
        try {
            let response = await fetchWithAuth(`${API_BASE_URL}/quotes/${quotationToArchive.quotation_id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...quotationToArchive, status: 3 })
            });
            if (!response.ok) {
                response = await fetchWithAuth(`${API_BASE_URL}/quotation/${id}/${quotationToArchive.date_created}/${quotationToArchive.quotation_id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ ...quotationToArchive, status: 3 })
                });
            }
            if (response.ok) {
                const archived = { ...quotationToArchive, status: 3 };
                setQuotations(prev => {
                    const filtered = prev.filter(q => q.quotation_id !== quotationToArchive.quotation_id);
                    if (quotationToArchive.quotation_id === latestActiveQuotationId) {
                        const remaining = filtered.filter(q => q.status === 1);
                        setLatestActiveQuotationId(remaining.length > 0
                            ? remaining.reduce((a, b) => b.quotation_id > a.quotation_id ? b : a).quotation_id
                            : null);
                    }
                    return filtered;
                });
                const sortFn = (a, b) => {
                    const diff = new Date(b.date_created || 0) - new Date(a.date_created || 0);
                    return diff !== 0 ? diff : b.quotation_id - a.quotation_id;
                };
                setArchivedQuotations(prev => [...prev, archived].sort(sortFn));
                showToast(`Quotation ${formatQuotationId(quotationToArchive.quotation_id)} archived successfully.`);
                setShowArchiveModal(false);
                setQuotationToArchive(null);
            } else {
                showToast('Failed to archive quotation.', 'danger');
            }
        } catch (error) {
            showToast('An error occurred while archiving the quotation.', 'danger');
        }
    };

    const handleArchiveCancel = () => { setShowArchiveModal(false); setQuotationToArchive(null); };

    const handleRemoveClick = (quotation) => { setQuotationToRemove(quotation); setShowRemoveModal(true); };

    const handleRemoveConfirm = async () => {
        if (!quotationToRemove) return;
        try {
            let response = await fetchWithAuth(`${API_BASE_URL}/quotes/${quotationToRemove.quotation_id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...quotationToRemove, status: 2 })
            });
            if (!response.ok) {
                response = await fetchWithAuth(`${API_BASE_URL}/quotation/${id}/${quotationToRemove.date_created}/${quotationToRemove.quotation_id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ ...quotationToRemove, status: 2 })
                });
            }
            if (response.ok) {
                const removed = { ...quotationToRemove, status: 2 };
                setQuotations(prev => prev.filter(q => q.quotation_id !== quotationToRemove.quotation_id));
                setArchivedQuotations(prev => prev.filter(q => q.quotation_id !== quotationToRemove.quotation_id));
                setRemovedQuotations(prev => [...prev, removed].sort((a, b) => b.quotation_id - a.quotation_id));
                showToast(`Quotation ${formatQuotationId(quotationToRemove.quotation_id)} removed successfully.`);
                setShowRemoveModal(false);
                setQuotationToRemove(null);
            } else {
                showToast('Failed to remove quotation.', 'danger');
            }
        } catch (error) {
            showToast('An error occurred while removing the quotation.', 'danger');
        }
    };

    const handleRemoveCancel = () => { setShowRemoveModal(false); setQuotationToRemove(null); };

    const handleUnarchive = async (quotation) => {
        try {
            let response = await fetchWithAuth(`${API_BASE_URL}/quotes/${quotation.quotation_id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...quotation, status: 0 })
            });
            if (!response.ok) {
                response = await fetchWithAuth(`${API_BASE_URL}/quotation/${id}/${quotation.date_created}/${quotation.quotation_id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ ...quotation, status: 0 })
                });
            }
            if (response.ok) {
                const unarchived = { ...quotation, status: 0 };
                const sortFn = (a, b) => {
                    const diff = new Date(b.date_created || 0) - new Date(a.date_created || 0);
                    return diff !== 0 ? diff : b.quotation_id - a.quotation_id;
                };
                setArchivedQuotations(prev => prev.filter(q => q.quotation_id !== quotation.quotation_id));
                setQuotations(prev => [...prev, unarchived].sort(sortFn));
                showToast(`Quotation ${formatQuotationId(quotation.quotation_id)} unarchived successfully.`);
            } else {
                showToast('Failed to unarchive quotation.', 'danger');
            }
        } catch (error) {
            showToast('An error occurred while unarchiving the quotation.', 'danger');
        }
    };

    const handleArchiveAllClick = () => setShowArchiveAllModal(true);

    const handleArchiveAllConfirm = async () => {
        const quotationsToArchive = quotations.filter(q => q.quotation_id !== latestActiveQuotationId);
        if (quotationsToArchive.length === 0) {
            showToast('No quotations to archive.', 'warning');
            setShowArchiveAllModal(false);
            return;
        }
        try {
            const archivePromises = quotationsToArchive.map(async (quote) => {
                let response = await fetchWithAuth(`${API_BASE_URL}/quotes/${quote.quotation_id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ ...quote, status: 3 })
                });
                if (!response.ok) {
                    response = await fetchWithAuth(`${API_BASE_URL}/quotation/${id}/${quote.date_created}/${quote.quotation_id}`, {
                        method: 'PUT',
                        body: JSON.stringify({ ...quote, status: 3 })
                    });
                }
                return response;
            });

            const results = await Promise.allSettled(archivePromises);
            const successfulIds = new Set(
                quotationsToArchive
                    .filter((_, i) => results[i].status === 'fulfilled' && results[i].value?.ok)
                    .map(q => q.quotation_id)
            );
            const failedCount = results.length - successfulIds.size;

            if (successfulIds.size > 0) {
                const sortFn = (a, b) => {
                    const diff = new Date(b.date_created || 0) - new Date(a.date_created || 0);
                    return diff !== 0 ? diff : b.quotation_id - a.quotation_id;
                };
                const archivedOnes = quotationsToArchive.filter(q => successfulIds.has(q.quotation_id)).map(q => ({ ...q, status: 3 }));
                setQuotations(prev => prev.filter(q => !successfulIds.has(q.quotation_id)));
                setArchivedQuotations(prev => [...prev, ...archivedOnes].sort(sortFn));
                if (failedCount > 0) {
                    showToast(`Archived ${successfulIds.size} quotation(s), but ${failedCount} failed.`, 'warning');
                } else {
                    showToast(`Successfully archived ${successfulIds.size} quotation(s).`);
                }
            } else {
                showToast('Failed to archive quotations.', 'danger');
            }
            setShowArchiveAllModal(false);
        } catch (error) {
            showToast('An error occurred while archiving quotations.', 'danger');
            setShowArchiveAllModal(false);
        }
    };

    const handleArchiveAllCancel = () => setShowArchiveAllModal(false);

    useEffect(() => {
        fetchQuotations();
        fetchCustomerDetails();
    }, [id, currentTenant?.id, currentTenant?.name, sites]);

    const tabBtn = (key, label, count) => (
        <button
            type="button"
            onClick={() => setActiveTab(key)}
            style={{
                padding: '10px 22px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === key ? `2px solid ${C.accent}` : '2px solid transparent',
                marginBottom: '-2px',
                fontWeight: activeTab === key ? 700 : 500,
                color: activeTab === key ? 'var(--theme-text)' : 'var(--theme-text-muted)',
                cursor: 'pointer',
                fontSize: '0.92rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'color 0.15s',
            }}
        >
            {label}
            {count > 0 && (
                <span style={{
                    background: activeTab === key ? C.accent : '#9ca3af',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '1px 7px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    lineHeight: '1.5',
                }}>
                    {count}
                </span>
            )}
        </button>
    );

    const activeQuoteCount = quotations.filter(q => q.status === 1).length;

    return (
        <div className="care-recipients-page">
            <div className="cr-content">
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="cr-page-hero">
                <div className="cr-page-hero-text">
                    <h1 className="cr-page-title">Quotations</h1>
                    <p className="cr-page-subtitle">{customerName || 'View and manage quotations for this recipient'}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div className="cr-stat-chips">
                        <div className="cr-stat-chip">
                            <div className="cr-stat-value cr-stat-value--primary">{quotations.length}</div>
                            <div className="cr-stat-label">Quotations</div>
                        </div>
                        <div className="cr-stat-chip">
                            <div className="cr-stat-value cr-stat-value--green">{activeQuoteCount}</div>
                            <div className="cr-stat-label">Active</div>
                        </div>
                    </div>
                    {activeTab === 'active' && quotations.length > 0 && latestActiveQuotationId && quotations.some(q => q.quotation_id !== latestActiveQuotationId) && (
                        <PermissionGuard
                            permission={PERMISSIONS.QUOTATION_ARCHIVE}
                            showDisabled
                            tooltip="You do not have permission to archive quotations"
                        >
                            <Button
                                variant="warning"
                                size="sm"
                                onClick={handleArchiveAllClick}
                                style={{ fontWeight: 600 }}
                            >
                                Archive All (Except Latest Active)
                            </Button>
                        </PermissionGuard>
                    )}
                </div>
            </div>

            {/* ── Content ─────────────────────────────────────────────────── */}
            <div>
                {/* Tab strip */}
                <div style={{ display: 'flex', borderBottom: `2px solid ${C.border}`, marginBottom: 20 }}>
                    {tabBtn('active', 'Quotations', quotations.length)}
                    {hasPermission(PERMISSIONS.QUOTATION_VIEW_ARCHIVED) && tabBtn('archived', 'Archived', archivedQuotations.length)}
                    {hasPermission(PERMISSIONS.QUOTATION_VIEW_REMOVED) && tabBtn('removed', 'Removed', removedQuotations.length)}
                </div>

                {/* Table card */}
                <div style={{ background: 'var(--theme-surface)', borderRadius: 12, border: '1px solid var(--theme-border)', boxShadow: '0 1px 2px var(--theme-shadow)', overflow: 'hidden' }}>
                    {activeTab === 'active' && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: C.headerBg }}>
                                    <th style={TH}>#</th>
                                    <th style={TH}>Date Created</th>
                                    <th style={TH}>Service Plan ID</th>
                                    <th style={TH}>Quotation ID</th>
                                    <th style={TH}>Tenant</th>
                                    <th style={TH}>Site</th>
                                    <th style={TH}>Status</th>
                                    <th style={TH}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quotations.length > 0 ? (
                                    quotations.map((quote, index) => {
                                        return (
                                            <tr
                                                key={quote.quotation_id}
                                                onClick={() => navigate(quotationDetailPath(id, quote))}
                                                style={{ background: index % 2 === 0 ? C.surface : C.rowAlt, cursor: 'pointer', transition: 'background 0.12s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = C.rowHover}
                                                onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? C.surface : C.rowAlt}
                                            >
                                                <td style={{ ...TD, color: C.textMuted, width: 48 }}>{index + 1}</td>
                                                <td style={TD}>{quote.date_created}</td>
                                                <td style={TD} onClick={e => e.stopPropagation()}>
                                                    <Link to={`/careplans/${id}/${quote.care_plan_id}`} style={{ color: C.blue, fontWeight: 600, textDecoration: 'none' }}>
                                                        {formatCarePlanId(quote.care_plan_id)}
                                                    </Link>
                                                </td>
                                                <td style={TD}>
                                                    <Link to={quotationDetailPath(id, quote)} style={{ color: C.blue, fontWeight: 600, textDecoration: 'none' }}>
                                                        {formatQuotationId(quote.quotation_id)}
                                                    </Link>
                                                </td>
                                                <td style={TD}>{quote.tenant_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                                <td style={TD}>{quote.site_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                                <td style={TD}>
                                                    {quote.status === 1 ? (
                                                        <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 20, padding: '3px 12px', fontSize: '0.78rem', fontWeight: 700 }}>
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span style={{ background: '#f3f4f6', color: C.textMuted, borderRadius: 20, padding: '3px 12px', fontSize: '0.78rem', fontWeight: 600 }}>
                                                            Inactive
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ ...TD, width: 200 }} onClick={e => e.stopPropagation()}>
                                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <Button
                                                            variant={quote.status === 1 ? 'outline-secondary' : 'primary'}
                                                            size="sm"
                                                            disabled={quote.status === 1 || loading[quote.quotation_id]}
                                                            onClick={() => handleActivateQuotation(quote.quotation_id)}
                                                            style={{ fontSize: '0.8rem', padding: '4px 12px', fontWeight: 600 }}
                                                        >
                                                            {loading[quote.quotation_id] ? 'Activating…' : quote.status === 1 ? 'Active' : 'Activate'}
                                                        </Button>
                                                        {quotations.length > 1 && quote.quotation_id !== latestActiveQuotationId && (
                                                            <>
                                                                <PermissionGuard
                                                                    permission={PERMISSIONS.QUOTATION_ARCHIVE}
                                                                    showDisabled
                                                                    tooltip="You do not have permission to archive quotations"
                                                                >
                                                                    <Button
                                                                        variant="outline-warning"
                                                                        size="sm"
                                                                        onClick={() => handleArchiveClick(quote)}
                                                                        title="Archive"
                                                                        style={{ padding: '4px 8px', lineHeight: 1 }}
                                                                    >
                                                                        <FaArchive size={12} />
                                                                    </Button>
                                                                </PermissionGuard>
                                                                <PermissionGuard
                                                                    permission={PERMISSIONS.QUOTATION_DELETE}
                                                                    showDisabled
                                                                    tooltip="You do not have permission to remove quotations"
                                                                >
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => handleRemoveClick(quote)}
                                                                        title="Remove permanently"
                                                                        style={{ padding: '4px 8px', lineHeight: 1 }}
                                                                    >
                                                                        <FaTrash size={12} />
                                                                    </Button>
                                                                </PermissionGuard>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontStyle: 'italic' }}>
                                            No quotations available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {activeTab === 'archived' && hasPermission(PERMISSIONS.QUOTATION_VIEW_ARCHIVED) && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: C.headerBg }}>
                                    <th style={TH}>#</th>
                                    <th style={TH}>Date Created</th>
                                    <th style={TH}>Service Plan ID</th>
                                    <th style={TH}>Quotation ID</th>
                                    <th style={TH}>Tenant</th>
                                    <th style={TH}>Site</th>
                                    <th style={TH}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {archivedQuotations.length > 0 ? (
                                    archivedQuotations.map((quote, index) => {
                                        return (
                                            <tr
                                                key={quote.quotation_id}
                                                onClick={() => navigate(quotationDetailPath(id, quote))}
                                                style={{ background: index % 2 === 0 ? C.surface : C.rowAlt, cursor: 'pointer', transition: 'background 0.12s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = C.rowHover}
                                                onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? C.surface : C.rowAlt}
                                            >
                                                <td style={{ ...TD, color: C.textMuted, width: 48 }}>{index + 1}</td>
                                                <td style={TD}>{quote.date_created}</td>
                                                <td style={TD} onClick={e => e.stopPropagation()}>
                                                    <Link to={`/careplans/${id}/${quote.care_plan_id}`} style={{ color: C.blue, fontWeight: 600, textDecoration: 'none' }}>
                                                        {formatCarePlanId(quote.care_plan_id)}
                                                    </Link>
                                                </td>
                                                <td style={TD}>
                                                    <Link to={quotationDetailPath(id, quote)} style={{ color: C.blue, fontWeight: 600, textDecoration: 'none' }}>
                                                        {formatQuotationId(quote.quotation_id)}
                                                    </Link>
                                                </td>
                                                <td style={TD}>{quote.tenant_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                                <td style={TD}>{quote.site_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                                <td style={{ ...TD, width: 160 }} onClick={e => e.stopPropagation()}>
                                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                        <PermissionGuard
                                                            permission={PERMISSIONS.QUOTATION_RESTORE}
                                                            showDisabled
                                                            tooltip="You do not have permission to unarchive quotations"
                                                        >
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={() => handleUnarchive(quote)}
                                                                style={{ fontSize: '0.8rem', padding: '4px 12px', fontWeight: 600 }}
                                                            >
                                                                Unarchive
                                                            </Button>
                                                        </PermissionGuard>
                                                        <PermissionGuard
                                                            permission={PERMISSIONS.QUOTATION_DELETE}
                                                            showDisabled
                                                            tooltip="You do not have permission to remove quotations"
                                                        >
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleRemoveClick(quote)}
                                                                title="Remove permanently"
                                                                style={{ padding: '4px 8px', lineHeight: 1 }}
                                                            >
                                                                <FaTrash size={12} />
                                                            </Button>
                                                        </PermissionGuard>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontStyle: 'italic' }}>
                                            No archived quotations
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {activeTab === 'removed' && hasPermission(PERMISSIONS.QUOTATION_VIEW_REMOVED) && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: C.headerBg }}>
                                    <th style={TH}>#</th>
                                    <th style={TH}>Date Created</th>
                                    <th style={TH}>Service Plan ID</th>
                                    <th style={TH}>Quotation ID</th>
                                    <th style={TH}>Tenant</th>
                                    <th style={TH}>Site</th>
                                </tr>
                            </thead>
                            <tbody>
                                {removedQuotations.length > 0 ? (
                                    removedQuotations.map((quote, index) => {
                                        return (
                                            <tr
                                                key={quote.quotation_id}
                                                onClick={() => navigate(quotationDetailPath(id, quote))}
                                                style={{ background: index % 2 === 0 ? C.surface : C.rowAlt, cursor: 'pointer', transition: 'background 0.12s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = C.rowHover}
                                                onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? C.surface : C.rowAlt}
                                            >
                                                <td style={{ ...TD, color: C.textMuted, width: 48 }}>{index + 1}</td>
                                                <td style={TD}>{quote.date_created}</td>
                                                <td style={TD} onClick={e => e.stopPropagation()}>
                                                    <Link to={`/careplans/${id}/${quote.care_plan_id}`} style={{ color: '#dc2626', fontWeight: 600, textDecoration: 'none' }}>
                                                        {formatCarePlanId(quote.care_plan_id)}
                                                    </Link>
                                                </td>
                                                <td style={TD}>
                                                    <Link to={quotationDetailPath(id, quote)} style={{ color: '#dc2626', fontWeight: 600, textDecoration: 'none' }}>
                                                        {formatQuotationId(quote.quotation_id)}
                                                    </Link>
                                                </td>
                                                <td style={TD}>{quote.tenant_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                                <td style={TD}>{quote.site_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontStyle: 'italic' }}>
                                            No removed quotations
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            </div>

            {/* ── Archive modal ────────────────────────────────────────────── */}
            <Modal show={showArchiveModal} onHide={handleArchiveCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Archive Quotation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to archive quotation {formatQuotationId(quotationToArchive?.quotation_id)}?
                    Archived quotations are hidden from the main list but remain accessible via the "Archived" tab and can be unarchived at any time.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleArchiveCancel}>Cancel</Button>
                    <Button variant="warning" onClick={handleArchiveConfirm}>Archive</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Remove modal ─────────────────────────────────────────────── */}
            <Modal show={showRemoveModal} onHide={handleRemoveCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Remove Quotation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to remove quotation {formatQuotationId(quotationToRemove?.quotation_id)}?</p>
                    <p className="text-danger mb-0">
                        <strong>Warning:</strong> Removing this quotation will effectively delete it from the platform.
                        It will no longer be accessible from any list and this action cannot be undone.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleRemoveCancel}>Cancel</Button>
                    <Button variant="danger" onClick={handleRemoveConfirm}>Remove</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Archive All modal ────────────────────────────────────────── */}
            <Modal show={showArchiveAllModal} onHide={handleArchiveAllCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Archive All Quotations</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Quotation <strong>{latestActiveQuotationId}</strong> (latest active) will be kept.</p>
                    <p>
                        <strong>{quotations.filter(q => q.quotation_id !== latestActiveQuotationId).length}</strong> quotation(s) will be archived and accessible via the "Archived" tab.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleArchiveAllCancel}>Cancel</Button>
                    <Button variant="warning" onClick={handleArchiveAllConfirm}>Archive All</Button>
                </Modal.Footer>
            </Modal>

            {toast.show && (
                <div className={`app-toast app-toast-${toast.variant}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
}

export default Quotations;
